import React, { useEffect, useRef, useState } from "react";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import { EffectFade, Navigation, Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";
import CloudinaryAdvancedImage from "@common/utils/CloudinaryAdvancedImage";
import apiUrl from "@common/utils/apiURL";
import useApi from "@common/utils/useApi";
import PlayIcon from "@common/assets/images/icons/play.svg";

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowWidth;
};

/**
 * Banner Component with Smart Type Controls
 *
 * @param {string} imgId - Image ID for static banners (fallback)
 * @param {string} type - Size modifier ('small' for compact banners)
 *
 * Smart Banner Selection:
 * - Fetches ALL active banners from CMS
 * - Automatically determines best banner type to display:
 *   1. If only video banners exist → Shows video banner
 *   2. If only slide banners exist → Shows slide carousel
 *   3. If mixed types → Separates and shows appropriate type
 * - Video-in-Slide: Slide banners with videoUrl show play button overlay
 *
 * Examples:
 * <Banner /> - Fetches all active banners, auto-detects type
 * <Banner type="small" /> - Small banner variant
 */
function Banner({ imgId, type, hideText = false }) {
  const videoRef = useRef();
  const [muteState, setMuteState] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const screenWidth = useWindowWidth();
  const [bannerData, setBannerData] = useState([]);

  // Always fetch ALL active banners from CMS
  const { data: cmsData, loading: cmsLoading } = useApi(
    apiUrl.banner.getActive
  );

  const muteUnmuteHandleChange = () => {
    if (muteState) {
      setMuteState(false);
    } else {
      setMuteState(true);
    }
  };

  // Get video URLs from CMS data
  const getVideoUrl = () => {
    if (bannerData.length > 0 && bannerData[0].type === "video") {
      const videoBanner = bannerData[0];
      if (screenWidth >= 768 && videoBanner.videoUrl) {
        return videoBanner.videoUrl;
      } else if (screenWidth < 768 && videoBanner.videoUrlMobile) {
        return videoBanner.videoUrlMobile;
      } else if (videoBanner.videoUrl) {
        return videoBanner.videoUrl;
      }
    }
    return "";
  };

  const videoUrl = getVideoUrl();

  // Update banner data when CMS data is loaded
  useEffect(() => {
    if (!cmsLoading && cmsData && cmsData.data) {
      const banners = Array.isArray(cmsData.data)
        ? cmsData.data
        : cmsData.data.banners || [];
      setBannerData(banners);
    }
  }, [cmsLoading, cmsData]);

  // Smart banner type detection - separate banners by type
  const videoBanners = bannerData.filter((banner) => banner.type === "video");
  const slideBanners = bannerData.filter((banner) => banner.type === "slide");
  const staticBanners = bannerData.filter((banner) => banner.type === "static");

  // Determine which banner type to display (priority: video > slide > static)
  let bannerType = "";
  let activeBanners = [];

  if (videoBanners.length > 0) {
    bannerType = "video";
    activeBanners = videoBanners;
  } else if (slideBanners.length > 0) {
    bannerType = "slide";
    activeBanners = slideBanners;
  } else if (staticBanners.length > 0) {
    bannerType = "static";
    activeBanners = staticBanners;
  }

  // Use appropriate data for slides
  const bannerSlideData =
    bannerType === "slide" ? activeBanners : [];

  // Handle video modal for video-in-slide feature
  const handleVideoClick = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentVideoUrl("");
  };

  // Control flags
  const showVideoControls = bannerType === "video";
  const showSlideControls = bannerType === "slide";
  const isStaticBanner = bannerType === "static";
  const bannerSize = type; // 'small' or undefined

  return (
    <>
      {/* Video Modal */}
      {videoModalOpen && (
        <div className="bs-video-modal" onClick={handleCloseVideoModal}>
          <div className="bs-video-modal__overlay"></div>
          <div
            className="bs-video-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="bs-video-modal__close"
              onClick={handleCloseVideoModal}
            >
              <span className="icon icon-simple-close"></span>
            </button>
            <div className="bs-video-modal__video-wrapper">
              <video
                controls
                autoPlay
                className="bs-video-modal__video"
                src={currentVideoUrl}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
      <div
        className={`bs-banner ${bannerType === "video" ? "bs-banner--typ-video" : ""
          } ${bannerSize === "small" ? "bs-banner--typ-small" : ""} ${bannerType === "slide" ? "bs-banner--typ-slide" : ""
          } ${isStaticBanner ? "bs-banner--typ-static" : ""}`}
      >
        {showVideoControls ? (
          <div className="bs-banner__video-wrap">
            <video
              controls={false}
              autoPlay={true}
              preload="none"
              playsInline={true}
              webkit-playsinline={"true"}
              muted={muteState}
              loop={true}
              className="bs-banner__video"
              ref={videoRef}
              src={videoUrl}
            />
            {!hideText && (
              <div className="bs-banner__slide-info">
                <Link
                  to={
                    bannerData.length > 0 && bannerData[0].link
                      ? bannerData[0].link
                      : "/collections"
                  }
                  className="bs-btn bs-btn__btn-solid bs-btn__btn-solid--inverse"
                >
                  explore now
                </Link>
              </div>
            )}
            {showVideoControls && (
              <button
                className="bs-banner__video-wrap__action"
                onClick={muteUnmuteHandleChange}
              >
                {!muteState ? (
                  <span className="icon icon-unmute"></span>
                ) : (
                  <span className="icon icon-mute"></span>
                )}
              </button>
            )}
          </div>
        ) : showSlideControls ? (
          <Swiper
            spaceBetween={30}
            effect={"fade"}
            navigation={true}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            speed={2000}
            modules={[EffectFade, Navigation, Autoplay]}
            className="bs-banner__swiper-container"
            loop={true}
          >
            {bannerSlideData.map((item, index) => {
              const imageUrl =
                window.innerWidth <= 1024 && item.imgIdMobile
                  ? item.imgIdMobile
                  : item.imgId;
              const hasVideo = item.videoUrl || item.videoUrlMobile;
              const videoUrlToPlay =
                screenWidth >= 768
                  ? item.videoUrl
                  : item.videoUrlMobile || item.videoUrl;

              return (
                <SwiperSlide className="bs-banner__swiper-slide" key={index}>
                  <div className="bs-banner__slide-image">
                    <CloudinaryAdvancedImage
                      imagePublicId={imageUrl}
                      className="bs-banner__image"
                      type="original"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "bottom",
                      }}
                    />
                    {hasVideo && (
                      <button
                        className="bs-banner__video-play-btn"
                        onClick={() => handleVideoClick(videoUrlToPlay)}
                        aria-label="Play video"
                      >
                        <img
                          src={PlayIcon}
                          alt="Play"
                          className="bs-banner__play-icon"
                        />
                      </button>
                    )}
                  </div>
                  {!hideText && (
                    <div className="bs-banner__slide-info">
                      {item.category && (
                        <label className="bs-banner__slide-category">
                          {item.category}
                        </label>
                      )}
                      <h2 className="bs-banner__slide-title">{item.title}</h2>
                      {item.link === "" ? null : (
                        <Link
                          to={item.link}
                          className="bs-btn bs-btn__btn-solid bs-btn__btn-solid--inverse"
                        >
                          Shop now
                        </Link>
                      )}
                    </div>
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div className="bs-banner__media">
            <CloudinaryAdvancedImage
              imagePublicId={imgId}
              className="bs-banner__image"
              type="original"
            />
          </div>
        )}
      </div>
    </>
  );
}

export default Banner;
